import { StatCard } from '@/components/StatCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizations } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import type { Organization } from '@octave/contracts';
import {
  Building2,
  ChevronRight,
  MapPin,
  Search,
  Smartphone,
  Trash2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeleteOrganizationDialog } from './organization/DeleteOrganizationDialog';

export function AllOrganizationsView() {
  const { data: organizations, isLoading, error } = useOrganizations();
  const navigate = useNavigate();
  const { saveTenantSlug } = useTenantSlug();
  const [searchText, setSearchText] = useState('');
  const [organizationToDelete, setOrganizationToDelete] =
    useState<Organization | null>(null);

  const filteredOrganizations = useMemo(() => {
    if (!organizations) return [];
    if (!searchText) return organizations;
    const lower = searchText.toLowerCase();
    return organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(lower) ||
        org.tenantSlug?.toLowerCase().includes(lower)
    );
  }, [organizations, searchText]);

  const stats = useMemo(() => {
    if (!organizations) return null;
    const totalSites = organizations.reduce(
      (sum, org) => sum + (org.sites?.length || 0),
      0
    );
    return {
      totalOrgs: organizations.length,
      totalSites,
    };
  }, [organizations]);

  const handleOrganizationClick = (org: Organization) => {
    saveTenantSlug(org.tenantSlug!);
    navigate(`/o/${org.tenantSlug}/lines`);
    window.location.reload();
  };

  const handleDeleteClick = (e: React.MouseEvent, org: Organization) => {
    e.stopPropagation();
    setOrganizationToDelete(org);
  };

  const handlePricePlanClick = (e: React.MouseEvent, org: Organization) => {
    e.stopPropagation();
    navigate(`/o/admin/${org.id}/plans`);
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div>
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96 mt-2" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
        <Skeleton className="h-10 w-full sm:w-80" />
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Toutes les Organisations
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gérer et surveiller toutes les organisations dans la plateforme
          </p>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
          <Building2 className="h-10 w-10 mb-3 text-slate-300" />
          <p className="text-sm font-medium text-red-500">
            Erreur lors du chargement des organisations
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-4 bg-white border-slate-200"
          >
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Toutes les Organisations
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Gérer et surveiller toutes les organisations dans la plateforme
        </p>
      </div>

      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={Building2}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            label="Organisations"
            value={stats.totalOrgs}
          />
          <StatCard
            icon={MapPin}
            iconBg="bg-sky-50"
            iconColor="text-sky-600"
            label="Centres de facturation"
            value={stats.totalSites}
          />
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative w-full sm:flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher une organisation..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 bg-white border-slate-200"
          />
        </div>
      </div>

      {/* Organizations List */}
      <div className="space-y-2">
        {filteredOrganizations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Building2 className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium">Aucune organisation trouvée</p>
            {searchText && (
              <p className="text-xs mt-1">Essayez de modifier votre recherche</p>
            )}
          </div>
        ) : (
          filteredOrganizations.map((org) => (
            <div
              key={org.id}
              className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 transition-all duration-200 hover:shadow-md hover:scale-[1.005] active:scale-[0.995] border border-transparent hover:border-emerald-100 cursor-pointer"
              onClick={() => handleOrganizationClick(org)}
            >
              {/* Icon */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 group-hover:bg-emerald-50 transition-colors">
                <Building2 className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">
                  {org.name}
                </p>
                {org.tenantSlug && (
                  <p className="text-xs text-slate-400 truncate mt-0.5">
                    {org.tenantSlug}
                  </p>
                )}
              </div>

              {/* Sites badge */}
              <Badge
                variant="outline"
                className="text-xs bg-violet-50 text-violet-600 border-violet-200 shrink-0 hidden sm:inline-flex"
              >
                {org.sites?.length || 0} C.F.
              </Badge>

              {/* Plan button */}
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50 shrink-0 hidden sm:inline-flex"
                onClick={(e) => handlePricePlanClick(e, org)}
              >
                <Smartphone className="h-3.5 w-3.5" />
                Forfaits mobiles
              </Button>

              {/* Delete button */}
              <Button
                variant="outline"
                size="sm"
                aria-label={`Supprimer ${org.name}`}
                className="border-slate-200 text-slate-400 hover:bg-red-50 hover:text-red-600 hover:border-red-200 shrink-0"
                onClick={(e) => handleDeleteClick(e, org)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>

              {/* Chevron */}
              <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
            </div>
          ))
        )}
      </div>

      {organizationToDelete && (
        <DeleteOrganizationDialog
          organizationId={organizationToDelete.id}
          organizationName={organizationToDelete.name}
          isOpen
          onClose={() => setOrganizationToDelete(null)}
        />
      )}
    </div>
  );
}
